import { Vehicle, VehicleAlgoliaRecord } from "@/types";
import { isLocalPhoto } from "./vehicle";

export interface ImageKitOptions {
    width?: number;
    quality?: number;
    format?: "auto" | "jpg" | "webp";
    aspectRatio?: string;
    crop?: "maintain_ratio" | "force" | "at_max";
}

const defaultOptions: ImageKitOptions = {
    width: 400,
    quality: 75,
    format: "auto",
    crop: "maintain_ratio",
};

const VEHICLE_CARD_QUALITY_LCP = 75;
const VEHICLE_CARD_QUALITY_BELOW_FOLD = 70;
const PRESS_LOGO_QUALITY = 75;

/** ImageKit endpoint id (same host as vehicle photos on ik.imagekit.io/joinsteer/...) */
const IMAGEKIT_ENDPOINT_ID = "joinsteer";

/**
 * True when the asset is served from ImageKit and accepts `?tr=` transformations.
 */
export const isImageKitUrl = (url: string): boolean =>
    Boolean(url && url.includes("ik.imagekit.io"));

function buildTransformationList(
    options: ImageKitOptions,
): string[] {
    const transformations: string[] = [];

    if (options.width) {
        transformations.push(`w-${options.width}`);
    }

    if (options.quality) {
        transformations.push(`q-${options.quality}`);
    }

    if (options.format) {
        transformations.push(`f-${options.format}`);
    }

    if (options.aspectRatio) {
        transformations.push(`ar-${options.aspectRatio}`);
    }

    if (options.crop) {
        transformations.push(`c-${options.crop}`);
    }

    return transformations;
}

/**
 * Remote (non-ImageKit) URLs: fetch via ImageKit's URL endpoint so width/quality apply.
 * @see https://docs.imagekit.io/features/image-transformation
 */
export const getExternalUrlViaImageKitProxy = (
    url: string,
    options: Partial<ImageKitOptions> = {},
): string => {
    if (!url) return url;

    const finalOptions = { ...defaultOptions, ...options };
    const tr = buildTransformationList(finalOptions).join(",");

    return `https://ik.imagekit.io/${IMAGEKIT_ENDPOINT_ID}/tr:${tr}/${encodeURIComponent(url)}`;
};

/**
 * Generate an ImageKit URL with transformation parameters
 * @param url - The original image URL
 * @param options - ImageKit transformation options
 * @param applyTransforms - If false, returns original URL (no `?tr=`)
 */
export const getImageKitUrl = (
    url: string,
    options: Partial<ImageKitOptions> = {},
    applyTransforms = true,
): string => {
    if (!applyTransforms || !url) return url;

    const finalOptions = { ...defaultOptions, ...options };
    const transformations = buildTransformationList(finalOptions);

    return `${url}?tr=${transformations.join(",")}`;
};

/**
 * Generate a srcset string for responsive images
 * @param url - The original image URL
 * @param widths - Array of widths to generate srcset for
 * @param options - Additional ImageKit options (quality, format, etc.)
 * @param applyTransforms - If false, returns empty string
 */
export const getImageKitSrcSet = (
    url: string,
    widths: number[] = [400, 800, 1200],
    options: Partial<ImageKitOptions> = {},
    applyTransforms = true,
): string => {
    if (!applyTransforms || !url) return "";

    return widths
        .map(
            (width) =>
                `${getImageKitUrl(
                    url,
                    { ...options, width },
                    applyTransforms,
                )} ${width}w`,
        )
        .join(",\n");
};

/**
 * Generate a sizes attribute string based on widths array
 * Mobile (<768px): 100vw, Desktop: max width from array
 * @param widths - Array of widths (same as srcset widths)
 */
export const getImageSizes = (widths: number[] = [400]): string => {
    const maxWidth = Math.max(...widths);
    return `(max-width: 768px) 100vw, ${maxWidth}px`;
};

/**
 * Relative storage paths cannot be proxied by ImageKit. The server also provides the list
 * of private dev hosts (`window.__IMAGEKIT__.bypassHosts`, from IMAGEKIT_BYPASS_HOSTS) whose
 * photos must be served as-is. That list is empty in preprod/prod, so nothing about the dev
 * setup leaks to the browser and every remote photo goes through the proxy.
 */
export const shouldBypassImageKitProxy = (url: string): boolean => {
    if (!url || isImageKitUrl(url)) {
        return false;
    }

    if (url.startsWith("/storage")) {
        return true;
    }

    if (typeof window === "undefined") {
        return false;
    }

    const bypassHosts = window.__IMAGEKIT__?.bypassHosts;
    if (!bypassHosts?.length) {
        return false;
    }

    try {
        const { hostname } = new URL(url, window.location.origin);
        return bypassHosts.includes(hostname.toLowerCase());
    } catch {
        return false;
    }
};

/**
 * Generate an image URL for a vehicle photo.
 * ImageKit-hosted photos use `?tr=`; private/dev storage URLs are served as-is;
 * other remote URLs use ImageKit's external fetch proxy.
 */
export const getVehicleImageUrl = (
    vehicle: Vehicle | VehicleAlgoliaRecord,
    photo: string,
    options: Partial<ImageKitOptions> = {},
): string => {
    if (isImageKitUrl(photo)) {
        return getImageKitUrl(photo, options, true);
    }

    // Private/dev origins (localhost, host.docker.internal, /storage) — no proxy.
    if (shouldBypassImageKitProxy(photo)) {
        return photo;
    }

    // Indexed "local" photos that are not ImageKit URLs (rare) — serve raw.
    if (isLocalPhoto(vehicle, photo)) {
        return photo;
    }

    return getExternalUrlViaImageKitProxy(photo, options);
};

/**
 * Srcset for vehicle photos at multiple widths.
 * Pass narrower widths (e.g. [264, 400, 528]) from listing cards; gallery uses the default wide set.
 */
export const getVehicleImageSrcSet = (
    vehicle: Vehicle | VehicleAlgoliaRecord,
    photo: string,
    widths: number[] = [320, 400, 637, 800, 1200],
    options: Partial<ImageKitOptions> = {},
): string => {
    return widths
        .map((width) => {
            const url = getVehicleImageUrl(vehicle, photo, {
                ...options,
                width,
            });
            return `${url} ${width}w`;
        })
        .join(",\n");
};

/** Sizes for vehicle cards in the home grid (~264px mobile, ~400px desktop). */
export const getVehicleCardImageSizes = (): string =>
    "(max-width: 768px) 264px, 400px";

/** Max layout slot widths (px) per `sizes` branch — mirrors ImageKitHelper::getVehicleCardSlotWidths(). */
const VEHICLE_CARD_SLOT_WIDTHS: Record<string, number[]> = {
    home: [334, 360, 400],
    "home-sold": [334, 360, 300],
    landing: [380, 360, 320],
    "similar-vehicles": [366, 360, 400],
    default: [264, 400],
};

/**
 * DPR covered by vehicle-card `srcset` descriptors — the browser applies the real device DPR
 * at selection time, so 2× entries are needed for sharp cards on high-density screens.
 */
const VEHICLE_CARD_SRCSET_DPR = 2;

/** Width descriptors for each layout slot at 1× and `VEHICLE_CARD_SRCSET_DPR`× (mirrors PHP). */
export function srcSetWidthsForSlotWidths(
    slotWidths: number[],
    dpr = VEHICLE_CARD_SRCSET_DPR,
): number[] {
    const widths = new Set<number>();

    for (const slot of slotWidths) {
        widths.add(slot);
        if (dpr > 1) {
            widths.add(Math.round(slot * dpr));
        }
    }

    return [...widths].sort((a, b) => a - b);
}

const VEHICLE_CARD_DEFAULT_WIDTH: Record<string, number> = {
    home: 334,
    "home-sold": 334,
    landing: 400,
    "similar-vehicles": 400,
    default: 264,
};

const VEHICLE_CARD_IMAGE_SIZES: Record<string, string> = {
    home: "(max-width: 47.999rem) calc(100vw - 4.875rem), (max-width: 63.999rem) calc(50vw - 1.5rem), min(calc((min(100vw, 80rem) - 4rem - 1rem) / 3), 400px)",
    "home-sold":
        "(max-width: 47.999rem) calc(100vw - 4.875rem), (max-width: 63.999rem) calc(50vw - 1.5rem), min(calc(25vw - 0.5rem), 300px)",
    landing:
        "(max-width: 47.999rem) calc(100vw - 2rem), (max-width: 63.999rem) calc(50vw - 1.5rem), min(calc(25vw - 0.5rem), 320px)",
    "similar-vehicles":
        "(max-width: 47.999rem) calc(100vw - 4.875rem), (max-width: 63.999rem) calc(50vw - 1.5rem), min(calc((min(100vw, 64rem) - 4.875rem - 2rem) / 3), 400px)",
    default: "(max-width: 768px) 264px, 400px",
};

export function getVehicleCardImageWidthsForLayout(
    layout: string = "default",
): number[] {
    const slots =
        VEHICLE_CARD_SLOT_WIDTHS[layout] ?? VEHICLE_CARD_SLOT_WIDTHS.default;

    return srcSetWidthsForSlotWidths(slots);
}

export function getVehicleCardDefaultImageWidthForLayout(
    layout: string = "default",
): number {
    return VEHICLE_CARD_DEFAULT_WIDTH[layout] ?? VEHICLE_CARD_DEFAULT_WIDTH.default;
}

export function getVehicleCardImageSizesForLayout(
    layout: string = "default",
): string {
    return VEHICLE_CARD_IMAGE_SIZES[layout] ?? VEHICLE_CARD_IMAGE_SIZES.default;
}

export function getVehicleCardImageQualityForLayout(
    layout: string = "default",
): number {
    return layout === "default"
        ? VEHICLE_CARD_QUALITY_LCP
        : VEHICLE_CARD_QUALITY_BELOW_FOLD;
}

export function getVehicleCardImageUrlForLayout(
    vehicle: Vehicle | VehicleAlgoliaRecord,
    photo: string,
    layout: string = "default",
): string {
    return getVehicleImageUrl(vehicle, photo, {
        width: getVehicleCardDefaultImageWidthForLayout(layout),
        quality: getVehicleCardImageQualityForLayout(layout),
    });
}

export function getVehicleCardImageSrcSetForLayout(
    vehicle: Vehicle | VehicleAlgoliaRecord,
    photo: string,
    layout: string = "default",
): string {
    return getVehicleImageSrcSet(
        vehicle,
        photo,
        getVehicleCardImageWidthsForLayout(layout),
        { quality: getVehicleCardImageQualityForLayout(layout) },
    );
}

const PRESS_LOGO_WIDTHS = [160, 260] as const;

export function getPressLogoSizes(): string {
    return "(min-width: 60rem) 260px, 160px";
}

export function getPressLogoUrl(
    url: string,
    width: number = 160,
): string {
    if (!url) {
        return "";
    }

    if (shouldBypassImageKitProxy(url)) {
        return url;
    }

    if (isImageKitUrl(url)) {
        return getImageKitUrl(url, { width, quality: PRESS_LOGO_QUALITY }, true);
    }

    return getExternalUrlViaImageKitProxy(url, {
        width,
        quality: PRESS_LOGO_QUALITY,
    });
}

export function getPressLogoSrcSet(url: string): string {
    if (!url || shouldBypassImageKitProxy(url)) {
        return "";
    }

    return PRESS_LOGO_WIDTHS.map(
        (width) => `${getPressLogoUrl(url, width)} ${width}w`,
    ).join(", ");
}
